import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { GraduationCap, ShieldCheck, ArrowLeft, MessageCircle, Flag } from "lucide-react";
import { toast } from "sonner";

const MENTOR_TYPE_LABEL = {
  DOCTORAL_GRADUATE: "Doctoral Graduate",
  FACULTY: "Faculty",
  DISSERTATION_COACH: "Dissertation Coach",
  RESEARCH_METHODOLOGY_MENTOR: "Research / Methodology Mentor",
};
const AVAILABILITY_LABEL = {
  AVAILABLE: "Available",
  LIMITED: "Limited",
  NOT_ACCEPTING: "Not currently accepting new requests",
};
const REPORT_REASONS = [
  { v: "MISREPRESENTATION", l: "Misrepresentation of credentials" },
  { v: "HARASSMENT", l: "Harassment" },
  { v: "SPAM", l: "Spam" },
  { v: "INAPPROPRIATE_CONDUCT", l: "Inappropriate conduct" },
  { v: "OTHER", l: "Other" },
];

export default function MentorDetail() {
  const { mid } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [area, setArea] = useState("");
  const [stageContext, setStageContext] = useState("");
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("MISREPRESENTATION");
  const [reportDesc, setReportDesc] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/mentors/${mid}`);
      setM(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      nav("/mentors");
    }
  }, [mid, nav]);

  useEffect(() => { load(); }, [load]);

  const sendConnect = async () => {
    if (!message.trim()) { toast.error("Please add a short introduction"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/mentors/${mid}/connect`, {
        message: message.trim(),
        area: area.trim(),
        stage_context: stageContext.trim(),
      });
      toast.success("Request sent — the mentor will see it in their inbox");
      setConnectOpen(false); setMessage(""); setArea(""); setStageContext("");
      // Refresh detail to update CTA
      await load();
      // Optional: nav to messages inbox
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendReport = async () => {
    setBusy(true);
    try {
      await api.post("/moderation/report-mentor", {
        mentor_id: mid, reason: reportReason, description: reportDesc.trim(),
      });
      toast.success("Report submitted — thank you");
      setReportOpen(false); setReportDesc("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!m) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const notAccepting = m.availability === "NOT_ACCEPTING";
  const activeConv = m.existing_conversation && m.existing_conversation.status === "ACTIVE";
  const pendingConv = m.existing_conversation && m.existing_conversation.status === "PENDING";

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="mentor-detail-page">
      <Button variant="ghost" size="sm" data-testid="mentor-back" onClick={() => nav("/mentors")}>
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to mentors
      </Button>

      <Card className="p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <GraduationCap className="h-7 w-7 text-primary" />
              <h1 className="font-serif text-3xl font-black tracking-tight" data-testid="mentor-name">{m.display_name}</h1>
              {m.verification_status === "VERIFIED" && (
                <Badge data-testid="mentor-verified" className="bg-emerald-100 text-emerald-800 border-emerald-200 border">
                  <ShieldCheck className="h-3 w-3 mr-1" /> Verified
                </Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {MENTOR_TYPE_LABEL[m.mentor_type]} · {m.discipline || "—"} · {m.degree_type || ""}
            </p>
            {m.headline && <p className="mt-3 font-serif text-lg italic">&ldquo;{m.headline}&rdquo;</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Availability</span>
            <span data-testid="mentor-availability" className="text-sm font-medium">{AVAILABILITY_LABEL[m.availability]}</span>
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-6">
          {m.bio && (
            <div>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Background</p>
              <p className="mt-1 text-sm whitespace-pre-wrap" data-testid="mentor-bio">{m.bio}</p>
            </div>
          )}
          {m.mentoring_approach && (
            <div>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Mentoring approach</p>
              <p className="mt-1 text-sm whitespace-pre-wrap">{m.mentoring_approach}</p>
            </div>
          )}
          {(m.expertise?.length > 0) && (
            <div>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Expertise</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {m.expertise.map(e => (
                  <span key={e} className="text-[11px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-muted text-muted-foreground">
                    {e.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(m.supported_stages?.length > 0) && (
            <div>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Supports stages</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {m.supported_stages.map(s => (
                  <span key={s} className="text-[11px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-muted text-muted-foreground">
                    {s.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(m.research_areas?.length > 0) && (
            <div className="md:col-span-2">
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Research areas</p>
              <p className="mt-1 text-sm">{m.research_areas.join(" · ")}</p>
            </div>
          )}
          {m.university && (
            <div>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Institution</p>
              <p className="mt-1 text-sm">{m.university}</p>
            </div>
          )}
          {m.years_experience != null && (
            <div>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Experience</p>
              <p className="mt-1 text-sm">{m.years_experience} years</p>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-6 border-t pt-5 flex flex-wrap gap-2">
          {activeConv ? (
            <Button data-testid="mentor-message-btn" onClick={() => nav("/messages")}>
              <MessageCircle className="h-4 w-4 mr-1" /> Message mentor
            </Button>
          ) : pendingConv ? (
            <Button variant="outline" disabled data-testid="mentor-pending-btn">Request pending</Button>
          ) : notAccepting ? (
            <Button variant="outline" disabled data-testid="mentor-not-accepting-btn">Not currently accepting new requests</Button>
          ) : (
            <Button data-testid="mentor-connect-btn" onClick={() => setConnectOpen(true)}>Request to connect</Button>
          )}
          <Button variant="ghost" size="sm" data-testid="mentor-report-btn" onClick={() => setReportOpen(true)}>
            <Flag className="h-4 w-4 mr-1" /> Report
          </Button>
        </div>
      </Card>

      {/* Connect modal (inline card) */}
      {connectOpen && (
        <Card className="p-5" data-testid="connect-form">
          <h2 className="font-serif text-xl font-black">Request to connect with {m.display_name}</h2>
          <p className="mt-1 text-xs text-muted-foreground">Introduce yourself briefly. Once accepted, you can message through your inbox.</p>
          <div className="mt-4 grid gap-3">
            <Input data-testid="connect-stage" placeholder="Your current stage (e.g. comps, proposal)" value={stageContext} onChange={e => setStageContext(e.target.value)} />
            <Input data-testid="connect-area" placeholder="Area of help (e.g. IRB preparation)" value={area} onChange={e => setArea(e.target.value)} />
            <Textarea data-testid="connect-message" rows={5} placeholder="Short introduction — who you are, what you're working on, why you're reaching out" value={message} onChange={e => setMessage(e.target.value)} />
          </div>
          <div className="mt-4 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConnectOpen(false)} disabled={busy}>Cancel</Button>
            <Button data-testid="connect-send" onClick={sendConnect} disabled={busy || !message.trim()}>Send request</Button>
          </div>
        </Card>
      )}

      {/* Report modal */}
      {reportOpen && (
        <Card className="p-5" data-testid="report-form">
          <h2 className="font-serif text-xl font-black">Report mentor</h2>
          <p className="mt-1 text-xs text-muted-foreground">Your report goes to Trust &amp; Safety. Your identity is not shared with the mentor.</p>
          <div className="mt-3 grid gap-3">
            <select data-testid="report-reason" className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={reportReason} onChange={e => setReportReason(e.target.value)}>
              {REPORT_REASONS.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
            <Textarea data-testid="report-desc" rows={4} maxLength={1000} placeholder="Optional details" value={reportDesc} onChange={e => setReportDesc(e.target.value)} />
          </div>
          <div className="mt-4 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setReportOpen(false)} disabled={busy}>Cancel</Button>
            <Button data-testid="report-send" onClick={sendReport} disabled={busy}>Submit report</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
